import * as Papa from "papaparse";
import { useCallback, useMemo, useRef, useState } from "react";

type Import =
  | {
      state: "idle";
    }
  | {
      state: "parsing";
    }
  | {
      state: "running" | "complete";

      rowCount: number;
      importCount: number;
      errorCount: number;
      errors: string[];

      // The remaining time in milliseconds
      remainingTime: number | null;
    }
  | {
      state: "error";

      error: Error;
    };

type usePapaParseProps<T> = {
  // The import batch size
  batchSize?: number;

  processBatch(batch: T[]): Promise<{
    imported: number;
    errors: string[];
  }>;
};

export function usePapaParse<T>({
  batchSize = 10,
  processBatch,
}: usePapaParseProps<T>) {
  const importIdRef = useRef<number>(0);

  const [importer, setImporter] = useState<Import>({
    state: "idle",
  });

  const reset = useCallback(() => {
    setImporter({
      state: "idle",
    });
    importIdRef.current += 1;
  }, []);

  // Runs rows through processBatch in batches, keeping the progress state. The
  // rows come from Papa for a CSV, or already parsed from another format (a
  // vCard file: importRows below).
  const runRows = useCallback(
    async (rows: T[], parseErrors: string[], importId: number) => {
      setImporter({
        state: "running",
        rowCount: rows.length,
        errorCount: parseErrors.length,
        errors: parseErrors,
        importCount: 0,
        remainingTime: null,
      });

      let totalTime = 0;
      for (let i = 0; i < rows.length; i += batchSize) {
        if (importIdRef.current !== importId) {
          return;
        }

        const batch = rows.slice(i, i + batchSize);
        try {
          const start = Date.now();
          const result = await processBatch(batch);
          totalTime += Date.now() - start;

          const meanTime = totalTime / (i + batch.length);
          setImporter((previous) => {
            if (previous.state === "running") {
              const importCount = previous.importCount + result.imported;
              return {
                ...previous,
                importCount,
                errorCount: previous.errorCount + result.errors.length,
                errors: [...previous.errors, ...result.errors],
                remainingTime: meanTime * (rows.length - i - batch.length),
              };
            }
            return previous;
          });
        } catch (error) {
          console.error("Failed to import batch", error);
          setImporter((previous) =>
            previous.state === "running"
              ? {
                  ...previous,
                  errorCount: previous.errorCount + batch.length,
                  errors: [
                    ...previous.errors,
                    error instanceof Error
                      ? error.message
                      : "Unknown batch import error",
                  ],
                }
              : previous,
          );
        }
      }

      setImporter((previous) =>
        previous.state === "running"
          ? {
              ...previous,
              state: "complete",
              remainingTime: null,
            }
          : previous,
      );
    },
    [batchSize, processBatch],
  );

  const parseCsv = useCallback(
    (file: File) => {
      setImporter({
        state: "parsing",
      });

      const importId = importIdRef.current;
      Papa.parse<T>(file, {
        header: true,
        skipEmptyLines: true,
        async complete(results) {
          if (importIdRef.current !== importId) {
            return;
          }
          await runRows(
            results.data,
            results.errors.map((error) => error.message),
            importId,
          );
        },
        error(error) {
          console.error(error);
          setImporter({
            state: "error",
            error,
          });
        },
        // Keep every field a string. The contact importer (the only consumer)
        // calls .trim()/.split() on values, so auto-typing a purely-numeric
        // cell (e.g. a company literally named "333") to a number throws and
        // takes down the whole batch of 10 rows.
        dynamicTyping: false,
      });
    },
    [runRows],
  );

  // The same import for rows that are already parsed. `read` turns the file
  // into rows; a file it cannot read, or that holds none, is the same error
  // state a broken CSV ends in.
  const importRows = useCallback(
    async (read: () => Promise<T[]>) => {
      setImporter({ state: "parsing" });
      const importId = importIdRef.current;
      try {
        const rows = await read();
        if (importIdRef.current !== importId) return;
        if (rows.length === 0) throw new Error("No rows found in this file");
        await runRows(rows, [], importId);
      } catch (error) {
        console.error(error);
        setImporter({
          state: "error",
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }
    },
    [runRows],
  );

  return useMemo(
    () => ({
      importer,
      parseCsv,
      importRows,
      reset,
    }),
    [importer, parseCsv, importRows, reset],
  );
}
